import { DELIVERY_STATE_KEY, restoreDeliverySelection, selectDeliveryEmirate, incompleteDeliveryAttributes, addDays, dateWithinRange, deliveryOptions, dubaiNow, validateSelection } from '@theme/rdb-delivery-rules';
import { Component } from '@theme/component';
import { CartUpdateEvent } from '@theme/events';
import { updateMinimumOrder, requireMinimumOrder } from '@theme/rdb-minimum-order';

let saveQueue = Promise.resolve();
const stateKey = DELIVERY_STATE_KEY;

class RosariumDelivery extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.abort?.abort();
    this.abort = new AbortController();
    this.config = JSON.parse(this.querySelector('[data-delivery-config]').textContent);
    this.clockBase = this.config.serverTime;
    this.clockStart = performance.now();
    this.root = this.config.root.replace(/\/?$/, '/');
    this.checkoutButton = document.querySelector('#checkout');
    this.originalCheckoutLabel = this.checkoutButton?.textContent;
    this.feedback = this.querySelector('[data-delivery-feedback]');
    this.state = restoreDeliverySelection(this.config.attributes[stateKey], this.now(), this.config.rules);
    this.calendarMonth = (dateWithinRange(this.state.date, this.now(), this.config.rules) ? this.state.date : dubaiNow(this.now()).date).slice(0, 7);
    const options = { signal: this.abort.signal };
    cancelAnimationFrame(this.priceFrame);
    const refreshPrices = () => {
      cancelAnimationFrame(this.priceFrame);
      this.priceFrame = requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.priceLabels();
        this.priceSummary();
      });
    };
    document.addEventListener('rdb:currencychange', refreshPrices, options);
    document.addEventListener('futureblink:currencychange', refreshPrices, options);
    this.addEventListener('change', (event) => this.change(event), options);
    this.addEventListener('click', (event) => this.handleClick(event), options);
    this.addEventListener('keydown', (event) => this.calendarKey(event), options);
    document.addEventListener('submit', (event) => {
      if (event.target.id !== 'cart-form' || event.submitter?.name === 'update') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.checkout();
    }, { capture: true, signal: this.abort.signal });
    document.addEventListener('visibilitychange', () => { if (!document.hidden) this.refresh(); }, options);
    window.addEventListener('pageshow', () => this.refresh(), options);
    window.addEventListener('focus', () => this.refresh(), options);
    window.addEventListener('storage', (event) => { if (event.key === 'rdb-delivery-updated') this.refresh(); }, options);
    this.timer = setInterval(() => this.revalidate(), 30000);
    this.render();
    this.refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.abort?.abort();
    clearInterval(this.timer);
    cancelAnimationFrame(this.priceFrame);
    clearTimeout(this.saveTimer);
  }

  updatedCallback() {
    super.updatedCallback();
    if (!this.config || !this.isConnected) return;
    this.config = JSON.parse(this.querySelector('[data-delivery-config]').textContent);
    this.feedback = this.querySelector('[data-delivery-feedback]');
    this.checkoutButton = document.querySelector('#checkout');
    this.cart = null;
    this.render();
    this.refresh();
  }

  now() { return new Date(this.clockBase + performance.now() - this.clockStart); }
  t(key) { return this.config.strings[key] || ''; }
  formatLocale(kind = 'date') {
    const language = (this.config.locale || 'en').split('-')[0];
    if (language === 'ar') return 'ar-AE-u-ca-gregory-nu-latn';
    if (language === 'de') return 'de-DE';
    return kind === 'money' ? 'en-AE' : 'en-GB';
  }
  money(amount, currency = this.config.currency) {
    const converted = window.rdbCurrencyPrices?.format(Math.round(amount * 100), currency);
    return converted || new Intl.NumberFormat(this.formatLocale('money'), { style: 'currency', currency, maximumFractionDigits: 2 }).format(amount);
  }
  priceText(element, text) {
    if (!element || (element.textContent === text && element.firstElementChild?.tagName === 'BDI')) return;
    const price = document.createElement('bdi');
    price.dir = 'auto';
    price.textContent = text;
    element.replaceChildren(price);
  }
  priceLabels() {
    for (const service of ['express', 'exact']) this.priceText(this.querySelector(`[data-price=${service}]`), `+ ${this.money(service === 'express' ? 75 : 50, 'AED')}`);
  }
  dateLabel(date, options = { day: 'numeric', month: 'short' }) { return new Intl.DateTimeFormat(this.formatLocale(), { ...options, timeZone: 'UTC' }).format(new Date(date + 'T12:00:00Z')); }
  time(minute) {
    if (minute === 1440 || minute === 0) return this.t('midnight');
    return new Intl.DateTimeFormat(this.formatLocale(), { hour: 'numeric', minute: '2-digit', hour12: this.config.locale !== 'de', timeZone: 'UTC' }).format(new Date(Date.UTC(2026, 0, 1, 0, minute)));
  }
  range(start, end) { return `${this.time(start)} – ${this.time(end)}`; }
  result() { return validateSelection(this.state, this.now(), this.config.rules); }
  feeReady(result) {
    if (!result.fee) return true;
    const variant = this.config.fees[this.state.service];
    return this.config.currency === 'AED' && variant?.available && variant.id > 0 && variant.price === result.fee * 100;
  }
  message(key, error = false) {
    this.feedback.textContent = this.t(key);
    this.feedback.dataset.error = String(error);
    this.querySelector('[data-save-retry]').hidden = key !== 'save_error';
  }

  async request(path, body) {
    const response = await fetch(this.root + path, {
      method: body ? 'POST' : 'GET',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : { cache: 'no-store' }),
    });
    const serverDate = Date.parse(response.headers.get('date') || '');
    if (Number.isFinite(serverDate)) { this.clockBase = serverDate; this.clockStart = performance.now(); }
    if (!response.ok) throw new Error('request');
    const data = await response.json();
    if (data.status || data.errors) throw new Error('cart');
    return data;
  }

  async refresh() {
    if (this.refreshing) return;
    this.refreshing = true;
    try {
      this.cart = await this.request('cart.js');
      if (!this.isConnected) return;
      // A header change in another page/tab must not be overwritten by cached cart UI.
      if (!this.saving && !this.saveFailed && !this.checkingOut) {
        this.state = restoreDeliverySelection(this.cart.attributes[stateKey], this.now(), this.config.rules);
      }
      this.revalidate();
      // Initial rendering can invalidate a restored choice before revalidation sees it.
      const storedSelection = this.cart.attributes[stateKey];
      const feeIds = Object.values(this.config.fees).map((variant) => variant.id).filter(Boolean);
      const staleFee = !this.result().valid && this.cart.items.some((item) => feeIds.includes(item.variant_id));
      if (!this.checkingOut && ((storedSelection && storedSelection !== JSON.stringify(this.state)) || staleFee)) this.save();
      this.summary();
    } catch {
      if (this.isConnected) { this.message('save_error', true); this.checkoutButton.disabled = true; }
    } finally {
      this.refreshing = false;
    }
  }

  revalidate() {
    const wasComplete = Boolean(this.state.emirate && this.state.date && (this.state.slot || this.state.service === 'express'));
    if (!dateWithinRange(this.state.date, this.now(), this.config.rules)) this.state.date = '';
    const valid = this.result().valid;
    if (wasComplete && !valid) {
      this.state.slot = '';
      this.message('expired', true);
      if (!this.checkingOut) this.save();
    }
    this.render();
  }

  change(event) {
    if (this.checkingOut) return;
    const target = event.target;
    if (target.matches('[data-emirate]')) {
      this.setEmirate(target.value);
      return;
    } else if (target.name === 'rdb_service') {
      this.state.service = target.value;
      this.state.slot = '';
    } else if (target.name === 'rdb_slot') {
      this.state.service = 'standard';
      this.state.slot = target.value;
    } else if (target.matches('[data-exact-time]')) {
      this.state.slot = target.value;
    } else return;
    this.render();
    this.save();
  }

  setEmirate(emirate) {
    if (this.checkingOut) return false;
    const state = selectDeliveryEmirate(this.state, emirate);
    if (!state) return false;
    this.state = state;
    if (!this.state.date) this.state.date = dubaiNow(this.now()).date;
    this.render();
    this.save();
    return true;
  }

  handleClick(event) {
    const button = event.target.closest('button');
    if (!button || button.disabled || this.checkingOut) return;
    if (button.hasAttribute('data-save-retry')) {
      this.save();
    } else if (button.hasAttribute('data-express-today')) {
      const date = dubaiNow(this.now()).date;
      if (!deliveryOptions({ ...this.state, date }, this.now(), this.config.rules).express) { this.render(); return; }
      this.state = { ...this.state, date, service: 'express', slot: '' };
      this.calendarMonth = date.slice(0, 7);
      this.querySelector('#rdb-calendar').hidden = true;
      this.querySelector('[data-calendar-toggle]').setAttribute('aria-expanded', 'false');
      this.render();
      this.save();
      this.querySelector('[name=rdb_service][value=express]').focus({ preventScroll: true });
    } else if (button.hasAttribute('data-date-shortcut')) {
      this.chooseDate(addDays(dubaiNow(this.now()).date, Number(button.dataset.dateShortcut)));
    } else if (button.hasAttribute('data-calendar-toggle')) {
      const calendar = this.querySelector('#rdb-calendar');
      calendar.hidden = !calendar.hidden;
      button.setAttribute('aria-expanded', String(!calendar.hidden));
      if (!calendar.hidden) this.renderCalendar();
    } else if (button.hasAttribute('data-month')) {
      const date = new Date(`${this.calendarMonth}-01T12:00:00Z`);
      date.setUTCMonth(date.getUTCMonth() + Number(button.dataset.month));
      this.calendarMonth = date.toISOString().slice(0, 7);
      this.renderCalendar();
    } else if (button.hasAttribute('data-calendar-date')) {
      this.chooseDate(button.dataset.calendarDate);
      this.querySelector('[data-calendar-toggle]').focus();
    }
  }

  chooseDate(date) {
    if (!dateWithinRange(date, this.now(), this.config.rules)) return;
    this.state.date = date;
    this.state.slot = '';
    this.calendarMonth = date.slice(0, 7);
    if (this.state.service === 'express' && date !== dubaiNow(this.now()).date) this.state.service = 'standard';
    this.querySelector('#rdb-calendar').hidden = true;
    this.querySelector('[data-calendar-toggle]').setAttribute('aria-expanded', 'false');
    this.render();
    this.save();
  }

  render() {
    const today = dubaiNow(this.now()).date;
    let options = deliveryOptions(this.state, this.now(), this.config.rules);
    if (!options.express && this.state.service === 'express') {
      this.state.service = 'standard'; this.state.slot = '';
      options = deliveryOptions(this.state, this.now(), this.config.rules);
    }
    this.querySelector('[data-emirate]').value = this.state.emirate;
    this.querySelector('[data-date-step]').disabled = !this.state.emirate || this.checkingOut;
    this.querySelector('[data-time-step]').disabled = !this.state.emirate || !options.dateValid || this.checkingOut;
    for (const button of this.querySelectorAll('[data-date-shortcut]')) {
      const date = addDays(today, Number(button.dataset.dateShortcut));
      button.setAttribute('aria-pressed', String(this.state.date === date));
      button.disabled = !dateWithinRange(date, this.now(), this.config.rules);
      button.querySelector('small').textContent = this.dateLabel(date);
    }
    const customDate = this.state.date && this.state.date !== today && this.state.date !== addDays(today, 1);
    this.querySelector('[data-calendar-choice]').textContent = customDate ? this.dateLabel(this.state.date) : this.t('choose_date');
    this.querySelector('[data-calendar-toggle]').setAttribute('aria-pressed', String(Boolean(customDate)));
    this.querySelectorAll('[name=rdb_service]').forEach((input) => { input.checked = input.value === this.state.service; });
    const slots = this.querySelector('[data-standard-slots]');
    slots.hidden = this.state.service !== 'standard';
    const focusedSlot = slots.contains(document.activeElement) ? document.activeElement.value : null;
    slots.replaceChildren(...options.standard.map((slot) => {
      const label = document.createElement('label'); label.className = 'rdb-delivery__window';
      const input = document.createElement('input'); input.type = 'radio'; input.name = 'rdb_slot'; input.value = slot.id; input.checked = this.state.slot === slot.id && this.state.service === 'standard';
      const span = document.createElement('span'); span.textContent = this.range(slot.startMinute, slot.endMinute);
      label.append(input, span); return label;
    }));
    if (focusedSlot) [...slots.querySelectorAll('input')].find((input) => input.value === focusedSlot)?.focus({ preventScroll: true });
    this.querySelector('[data-no-standard]').hidden = options.standard.length > 0 || this.state.service !== 'standard';
    const express = this.querySelector('[data-express-service]');
    const expressToday = deliveryOptions({ ...this.state, date: today }, this.now(), this.config.rules).express;
    express.hidden = this.state.emirate !== 'dubai';
    express.dataset.unavailable = String(!options.express);
    express.querySelector('input').disabled = !options.express;
    express.querySelector('[data-express-today]').hidden = !expressToday || this.state.date === today;
    const reason = this.state.emirate !== 'dubai' ? 'express_dubai_only' : expressToday ? 'express_today_only' : this.state.date !== today ? 'express_today_hours' : 'express_hours';
    express.querySelector('[data-express-arrival]').textContent = options.express
      ? this.t(options.express.crossesMidnight ? 'express_tomorrow' : 'express_arrival').replace('__time__', this.time(options.express.arrivalMinute))
      : this.t(reason).replace('__start__', this.time(this.config.rules.expressStartMinute)).replace('__end__', this.time(this.config.rules.expressEndMinute - 60));
    this.priceLabels();
    this.querySelector('[data-exact-controls]').hidden = this.state.service !== 'exact';
    const select = this.querySelector('[data-exact-time]');
    select.replaceChildren(new Option(this.t('hour_placeholder'), ''), ...options.exact.map((slot) => new Option(this.range(slot.minute, slot.minute + 60), slot.id)));
    select.value = this.state.service === 'exact' ? this.state.slot : '';
    select.disabled = !options.exact.length;
    this.querySelector('[data-no-exact]').hidden = options.exact.length > 0;
    if (!this.querySelector('#rdb-calendar').hidden) this.renderCalendar();
    this.summary();
  }

  renderCalendar() {
    const focusedDate = this.contains(document.activeElement) ? document.activeElement.dataset.calendarDate : null;
    const today = dubaiNow(this.now()).date;
    const last = addDays(today, this.config.rules.maxDays);
    if (this.calendarMonth < today.slice(0, 7)) this.calendarMonth = today.slice(0, 7);
    if (this.calendarMonth > last.slice(0, 7)) this.calendarMonth = last.slice(0, 7);
    this.querySelector('[data-calendar-month]').textContent = this.dateLabel(this.calendarMonth + '-01', { month: 'long', year: 'numeric' });
    this.querySelector('[data-month="-1"]').disabled = this.calendarMonth === today.slice(0, 7);
    this.querySelector('[data-month="1"]').disabled = this.calendarMonth === last.slice(0, 7);
    const weekdays = this.querySelector('[data-weekdays]');
    weekdays.replaceChildren(...Array.from({ length: 7 }, (_, index) => { const span = document.createElement('span'); span.textContent = this.dateLabel(addDays('2026-09-07', index), { weekday: 'short' }); return span; }));
    const grid = this.querySelector('[data-calendar-days]');
    const first = this.calendarMonth + '-01';
    const offset = (new Date(first + 'T12:00:00Z').getUTCDay() + 6) % 7;
    const nodes = Array.from({ length: offset }, () => document.createElement('span'));
    for (let i = 0; i < 31; i++) {
      const date = addDays(first, i);
      if (!date.startsWith(this.calendarMonth)) break;
      const button = document.createElement('button'); button.type = 'button'; button.dataset.calendarDate = date;
      button.textContent = String(i + 1); button.disabled = !dateWithinRange(date, this.now(), this.config.rules);
      button.setAttribute('aria-label', this.dateLabel(date, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }));
      button.setAttribute('aria-pressed', String(date === this.state.date));
      if (date === today) button.setAttribute('aria-current', 'date');
      nodes.push(button);
    }
    grid.replaceChildren(...nodes);
    if (focusedDate) this.querySelector(`[data-calendar-date="${focusedDate}"]`)?.focus({ preventScroll: true });
  }

  calendarKey(event) {
    if (!event.target.matches('[data-calendar-date]')) return;
    const direction = getComputedStyle(this.querySelector('[data-calendar-days]')).direction === 'rtl' ? -1 : 1;
    const delta = { ArrowLeft: -direction, ArrowRight: direction, ArrowUp: -7, ArrowDown: 7 }[event.key];
    if (!delta) return;
    event.preventDefault();
    const date = addDays(event.target.dataset.calendarDate, delta);
    if (!dateWithinRange(date, this.now(), this.config.rules)) return;
    this.calendarMonth = date.slice(0, 7); this.renderCalendar();
    this.querySelector(`[data-calendar-date="${date}"]`)?.focus();
  }

  selectedTime() {
    const options = deliveryOptions(this.state, this.now(), this.config.rules);
    if (this.state.service === 'express') return this.querySelector('[data-express-arrival]').textContent;
    if (this.state.service === 'exact') { const slot = options.exact.find((slot) => slot.id === this.state.slot); return slot ? this.range(slot.minute, slot.minute + 60) : ''; }
    const slot = options.standard.find((slot) => slot.id === this.state.slot);
    return slot ? this.range(slot.startMinute, slot.endMinute) : '';
  }

  summary() {
    const result = this.result();
    const ready = result.valid && this.feeReady(result);
    const minimumReady = this.cart && updateMinimumOrder(this.cart).allowed;
    this.checkoutButton.disabled = !ready || !minimumReady || this.saving || this.saveFailed || this.checkingOut;
    const summary = document.querySelector('[data-delivery-summary]');
    const emirate = this.querySelector('[data-emirate]').selectedOptions[0]?.textContent;
    summary.textContent = result.valid ? `${emirate} · ${this.dateLabel(this.state.date, { day: 'numeric', month: 'long', year: 'numeric' })} · ${this.selectedTime()}` : this.t('incomplete');
    if (result.valid && !ready) this.message('unavailable', true);
    this.priceSummary(result);
    if (this.cart) document.querySelector('.cart__total-label').textContent = this.t('total_label');
    const selection = { state: { ...this.state }, saving: Boolean(this.saving), failed: Boolean(this.saveFailed), checkingOut: Boolean(this.checkingOut) };
    const signature = JSON.stringify(selection);
    if (signature !== this.selectionSignature) {
      this.selectionSignature = signature;
      document.dispatchEvent(new CustomEvent('rdb:delivery-selection', { detail: selection }));
    }
  }

  priceSummary(result = this.result()) {
    this.priceText(document.querySelector('[data-delivery-fee]'), result.fee ? this.money(result.fee, 'AED') : this.t('free'));
    if (this.cart) {
      const feeIds = Object.values(this.config.fees).map((variant) => variant.id).filter(Boolean);
      const feeLines = this.cart.items.filter((item) => feeIds.includes(item.variant_id));
      const charges = this.config.feeCharges || [];
      const receiptMatches = this.config.cartTotal === this.cart.total_price && feeLines.every((item) => charges.some((charge) => charge.key === item.key && charge.quantity === item.quantity));
      const existingFees = feeLines.reduce((sum, item) => sum + (receiptMatches ? charges.find((charge) => charge.key === item.key).effective : item.final_line_price), 0);
      const total = this.money((this.cart.total_price - existingFees) / 100 + (result.valid && this.config.currency === 'AED' ? result.fee : 0));
      const totalElement = document.querySelector('[ref=cartTotal]');
      if (totalElement) { this.priceText(totalElement, total); totalElement.setAttribute('value', total); }
    }
  }

  deliveryAttributes(state) {
    const result = validateSelection(state, this.now(), this.config.rules);
    const valid = result.valid;
    const deliveryDate = valid && state.service === 'express' ? result.arrival.date : state.date;
    return {
      ...incompleteDeliveryAttributes(state),
      'Delivery Method': valid ? 'Delivery' : '',
      'Delivery Emirate': valid ? state.emirate : '',
      'Delivery Date': valid ? deliveryDate : '',
      'Delivery Day': valid ? this.dateLabel(deliveryDate, { weekday: 'long' }) : '',
      'Delivery Time': valid ? this.selectedTime() : '',
      'Delivery Service': valid ? this.t(state.service) : '',
      'Delivery Timezone': valid ? 'Asia/Dubai (UTC+4)' : '',
      'Delivery Surcharge (AED)': valid ? String(validateSelection(state, this.now(), this.config.rules).fee) : '',
    };
  }

  save() {
    clearTimeout(this.saveTimer);
    this.saveFailed = false;
    this.saving = true;
    this.summary();
    this.saveTimer = setTimeout(() => {
      const state = { ...this.state };
      const attributes = this.deliveryAttributes(state);
      this.message('saving');
      saveQueue = saveQueue.catch(() => {}).then(async () => {
        const currentCart = await this.request('cart.js');
        const feeIds = Object.values(this.config.fees).map((variant) => variant.id).filter(Boolean);
        const updates = Object.fromEntries(currentCart.items.filter((item) => feeIds.includes(item.variant_id)).map((item) => [item.key, 0]));
        const removedFees = Object.keys(updates).length > 0;
        const cart = await this.request('cart/update.js', { attributes, ...(removedFees ? { updates, sections: [this.config.sectionId], sections_url: this.config.cartUrl } : {}) });
        if (cart.attributes[stateKey] !== attributes[stateKey]) throw new Error('readback');
        if (removedFees && this.isConnected) this.dispatchEvent(new CartUpdateEvent(cart, this.config.sectionId, { source: 'rdb-delivery', itemCount: cart.item_count, sections: cart.sections }));
        return cart;
      });
      saveQueue.then((cart) => {
        if (!this.isConnected || JSON.stringify(this.state) !== JSON.stringify(state)) return;
        this.cart = cart; this.saving = false; this.saveFailed = false; this.message('saved'); this.summary();
        document.dispatchEvent(new CustomEvent('rdb:delivery-saved'));
      }).catch(() => {
        if (this.isConnected && JSON.stringify(this.state) === JSON.stringify(state)) { this.saving = false; this.saveFailed = true; this.message('save_error', true); this.summary(); }
      });
    }, 150);
  }

  async checkout() {
    if (this.checkingOut || this.saving) return;
    let result = this.result();
    if (!result.valid || !this.feeReady(result)) { this.message(result.valid ? 'unavailable' : 'incomplete', true); this.scrollIntoView({ behavior: 'instant', block: 'start' }); return; }
    this.checkingOut = true;
    this.checkoutButton.textContent = this.t('checkout_busy');
    this.querySelector('[data-emirate]').disabled = true;
    this.render();
    try {
      await saveQueue.catch(() => {});
      let cart = await this.request('cart.js');
      this.cart = cart;
      if (!requireMinimumOrder(cart)) throw new Error('minimum-order');
      result = this.result();
      if (!result.valid || !this.feeReady(result)) throw new Error('expired');
      const feeIds = Object.values(this.config.fees).map((variant) => variant.id).filter(Boolean);
      if (!cart.items.some((item) => !feeIds.includes(item.variant_id))) throw new Error('empty');
      const feeVariant = result.fee ? this.config.fees[this.state.service] : null;
      const fees = cart.items.filter((item) => feeIds.includes(item.variant_id));
      const keep = fees.find((item) => item.variant_id === feeVariant?.id && item.quantity === 1 && item.final_line_price === result.fee * 100);
      const updates = Object.fromEntries(fees.filter((item) => item.key !== keep?.key).map((item) => [item.key, 0]));
      if (Object.keys(updates).length) cart = await this.request('cart/update.js', { updates });
      if (feeVariant && !keep) await this.request('cart/add.js', { items: [{ id: feeVariant.id, quantity: 1, properties: { _rdb_delivery_fee: 'v1', _rdb_delivery_service: this.state.service } }] });
      const attributes = this.deliveryAttributes(this.state);
      const saved = await this.request('cart/update.js', { attributes, ...(result.fee ? { sections: [this.config.sectionId], sections_url: this.config.cartUrl } : {}) });
      cart = await this.request('cart.js');
      const finalFees = cart.items.filter((item) => feeIds.includes(item.variant_id));
      if (result.fee) {
        const receiptHTML = saved.sections?.[this.config.sectionId];
        if (!receiptHTML) throw new Error('fee-readback');
        const receiptDocument = new DOMParser().parseFromString(receiptHTML, 'text/html');
        const receipt = JSON.parse(receiptDocument.querySelector('[data-delivery-config]')?.textContent || 'null');
        const charge = receipt?.feeCharges?.[0];
        if (receipt?.cartTotal !== cart.total_price || receipt?.attributes?.[stateKey] !== attributes[stateKey] || receipt?.feeCharges?.length !== 1 || charge?.id !== feeVariant.id || charge?.quantity !== 1 || charge?.key !== finalFees[0]?.key || charge?.original !== result.fee * 100) throw new Error('fee-readback');
        this.cart = cart;
        this.config.cartTotal = receipt.cartTotal;
        this.config.feeCharges = receipt.feeCharges;
        if (charge.effective !== result.fee * 100) throw new Error('fee-discount');
      }
      if (cart.attributes[stateKey] !== attributes[stateKey] || (result.fee ? finalFees.length !== 1 || finalFees[0].variant_id !== feeVariant.id || finalFees[0].quantity !== 1 || finalFees[0].final_line_price !== result.fee * 100 : finalFees.length !== 0)) throw new Error('fee-readback');
      if (!this.result().valid) throw new Error('expired');
      this.cart = cart;
      if (!requireMinimumOrder(cart)) throw new Error('minimum-order');
      window.location.assign(this.root + 'checkout');
    } catch (error) {
      this.checkingOut = false;
      this.querySelector('[data-emirate]').disabled = false;
      this.checkoutButton.textContent = this.originalCheckoutLabel;
      this.render();
      if (error.message !== 'minimum-order') this.message(error.message === 'expired' ? 'expired' : error.message === 'fee-discount' ? 'fee_discount' : 'save_error', true);
      if (error.message === 'expired') this.refresh();
    }
  }
}

if (!customElements.get('rdb-delivery')) customElements.define('rdb-delivery', RosariumDelivery);
